// MUS-496 — remember the tier a level was last played at, for this app session.
//
// Entering a level used to always jump to the highest completed tier, so a user
// reviewing an earlier tier of a beaten level was bounced back to the top on
// every refresh or in-app navigation. The approved behavior is that a level
// reopens on the tier you were last on, and only falls back to progress-derived
// tier selection on a fresh app open.
//
// sessionStorage carries exactly that lifetime: it survives a reload and any
// number of route changes within the tab/webview, and is discarded when the app
// is quit. Nothing here is authoritative: the createLevelSession saga clamps
// the play-session tier when the level's tier list loads, and on a tier list
// failure caps it at the startup tier count when that is known. A missing,
// stale, or hand-edited entry can only cost the user the convenience.

import logger from 'Utils/Logger';

const STORAGE_KEY = 'museflow.lastPlayedTierByLevel';

type TierByLevel = Record<string, number>;

/** sessionStorage throws in private-mode Safari and is absent in tests/SSR.
 *  Every access goes through here so a failure degrades to "no memory". */
function getStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function readAll(): TierByLevel {
  const store = getStore();
  if (!store) return {};
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    // Keep only well-formed entries; a corrupt one must not poison the rest.
    return Object.entries(parsed as Record<string, unknown>).reduce(
      (acc: TierByLevel, [level, tier]) => {
        if (isValidTier(tier)) acc[level] = tier;
        return acc;
      },
      {},
    );
  } catch (err) {
    logger.warn('[TierMemory] unreadable tier memory, ignoring', err);
    return {};
  }
}

function isValidTier(tier: unknown): tier is number {
  return typeof tier === 'number' && Number.isInteger(tier) && tier >= 1;
}

function isValidLevel(levelNumber: unknown): levelNumber is number {
  return typeof levelNumber === 'number' && Number.isInteger(levelNumber) && levelNumber >= 1;
}

/** Record the tier the user selected or is playing on `levelNumber`. */
export function rememberTierForLevel(levelNumber: number, tier: number): void {
  if (!isValidLevel(levelNumber) || !isValidTier(tier)) {
    logger.warn('[TierMemory] refusing to remember malformed level/tier', { levelNumber, tier });
    return;
  }
  const store = getStore();
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify({ ...readAll(), [levelNumber]: tier }));
  } catch (err) {
    logger.warn('[TierMemory] could not persist tier memory', err);
  }
}

/** The tier `levelNumber` was last played at this app session, or null when the
 *  level has not been entered since the app opened. */
export function recallTierForLevel(levelNumber: number): number | null {
  if (!isValidLevel(levelNumber)) return null;
  const remembered = readAll()[String(levelNumber)];
  return isValidTier(remembered) ? remembered : null;
}

/** Drop the whole memory. Exposed for tests and for sign-out, where the next
 *  user must not inherit the previous one's position. */
export function clearTierMemory(): void {
  const store = getStore();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch (err) {
    logger.warn('[TierMemory] could not clear tier memory', err);
  }
}
